/**
 * Scoring selection (PPR, superflex, rookies, week, research depth), the page URL built from
 * it, iframe persistent params, CSV/print export, and the segmented-control thumb.
 *
 * Calls on sibling mixins via `this`: RowLoading (cancelLoadRows), UserLeague
 * (selectedUserLeagueId, selectedUserLeagueText, cachedLeagueData), Filtering
 * (selectedFantasyPosition), CoreState (basePath, scoringModalOpen, loadingText).
 */
import { OFFENSIVE_FLEX_ROSTER_SPOTS } from './../../helpers/RosterSpotHelper.js';
import { exportToCsv } from './../../helpers/ExportHelper.js';

export default () => ({
    superflex: false,
    rookiesOnly: false,
    pprScoring: '',
    weekFilterOpen: false,
    exportContainerOpen: false,
    // disabling print option for initial rebrand release because this container is not working
    // and we do not have updated print styles anyways.
    exportContainerOptionPrint: false,
    displayAlert: false,
    alertMessage: '',
    selectedWeek: null,
    currentWeek: null,

    buildUrl() {
        let windowUrl = new URL(window.location);
        let urlParts = [this.basePath];

        if (this.selectedWeek) {
            urlParts.push(this.selectedWeek);
        }

        if (this.selectedFantasyPosition !== '') {
            let fantasyPositionForUrl = this.selectedFantasyPosition.toLowerCase();

            // --- For user leagues that selected their specific flex roster spot, we kee
            // --- the generic "flex" in the URL
            const selectedUserLeague = this.cachedLeagueData[this.selectedUserLeagueId]?.leagueData?.mvpBoardScoring;
            if (selectedUserLeague && OFFENSIVE_FLEX_ROSTER_SPOTS.includes(this.selectedFantasyPosition)) {
                fantasyPositionForUrl = 'flex';
            }
            urlParts.push(fantasyPositionForUrl);
        }
        let pprSuperflexSlug = this.getPprSuperflexSlug();
        if (pprSuperflexSlug !== '') {
            urlParts.push(pprSuperflexSlug);
        }
        if (
            ['rankings', 'weekly-rankings', 'ros-rankings'].includes(this.basePath) &&
            (this.researchDepth === 'projections' || this.researchDepth === 'analysis')
        ) {
            urlParts.push(this.researchDepth);
        }
        if (this.rookiesOnly) {
            urlParts.push('rookies');
        }
        let url = windowUrl.origin + '/' + urlParts.join('/');
        // Remove any trailing slashes
        url = url.replace(/\/+$/, '');
        return url;
    },

    updateUrl() {
        if (this.disableUrlUpdates) {
            return;
        }
        setTimeout(() => {
            let url = this.buildUrl();
            window.history.pushState({}, '', url);
        }, 250);
    },

    // Adds iframe context, such as partnerId, to shared rankings AJAX endpoints.
    appendPersistentParams(url) {
        const params = new URLSearchParams(this.persistentParams);
        const queryString = params.toString();
        if (!queryString) {
            return url;
        }
        return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
    },

    prepareCsvData() {
        let data = [];
        // Add rank, team, player, fantasy position to the start of the attributes and headers list
        let headers = ['Rank', 'Team', 'Player', 'Fantsy Position'];
        let attributes = [];
        this.$refs.rankingsTable.querySelectorAll('thead th').forEach(th => {
            // If has data-attribute, add it to the attributes list
            if (th.dataset.attribute) {
                let header = th.textContent.trim();
                if (th.dataset.colgroup) {
                    header = `${th.dataset.colgroup.charAt(0).toUpperCase()}${th.dataset.colgroup.slice(1)} ${header}`;
                }
                headers.push(header);
                attributes.push(th.dataset.attribute);
            }
        });
        data.push(headers);
        Array.from(this.$refs.rankingsTable.querySelectorAll('tbody[data-player-row]'))
            .filter(tbody => tbody.offsetParent !== null)
            .forEach((tbody, index) => {
                let row = [];
                row.push(index + 1); // Rank
                row.push(tbody.querySelector('td.player-name .player-details-group__team-position-container > span')?.innerText || ''); // Team
                row.push(tbody.dataset.playerName || ''); // Player
                row.push(tbody.dataset.fantasyPosition || ''); // Fantasy Position
                attributes.forEach(attr => {
                    let td = tbody.querySelector(`td[data-attribute="${attr}"]`);
                    let value = '';
                    if (td) {
                        value = td.dataset.value || td.textContent.trim();
                    }
                    if (attr === 'comment') {
                        // Remove any newlines from comments, and make commas safe for CSV
                        value = `"${value.replace(/[\r\n]+/g, ' ').replace(/"/g, '""')}"`;
                    }
                    row.push(value);
                });
                data.push(row);
            });
        return data;
    },
    handleExport() {
        // If a user league is selected, we cannot export via this method,
        // instead we are going to display an heads up message to direct them
        // to the DWR for exporting.
        if (this.selectedUserLeagueId) {
            const filename = `${this.selectedUserLeagueText.replace(/\s+/g, '-').toLowerCase()}-${this.basePath}`;
            exportToCsv(filename, this.prepareCsvData());
            return;
        }
        const form = document.querySelector('#sharedParams');
        const params = new URLSearchParams(new FormData(form));
        const exportUrl = '/' + this.basePath + '/export?' + params.toString();
        window.open(exportUrl, '_blank');
    },

    getPprSuperflexSlug() {
        if (this.superflex) {
            return this.pprScoring !== '' ? `${this.pprScoring}-superflex` : 'superflex';
        }
        return this.pprScoring || '';
    },

    handleResearchDepthChange(value, index) {
        this.researchDepth = value;
        this.toggleActive = index;
        this.cancelLoadRows();
        this.updateUrl();
    },

    handlePprScoringChange(value) {
        this.cancelLoadRows();
        this.loadingText = 'Loading     Scoring...'.split('');
        this.scoringModalOpen = true;
        this.pprScoring = value;
        this.updateUrl();
        this.selectedUserLeagueId = null;
        this.selectedUserLeagueText = 'Use My League';
    },

    handleSuperflexChange() {
        this.cancelLoadRows();
        this.loadingText = 'Loading     Scoring...'.split('');
        this.scoringModalOpen = true;
        this.updateUrl();
        this.selectedUserLeagueId = null;
        this.selectedUserLeagueText = 'Use My League';
    },

    handleRookiesOnlyChange() {
        this.cancelLoadRows();
        this.loadingText = 'Loading     Scoring...'.split('');
        this.scoringModalOpen = true;
        this.updateUrl();
        this.selectedUserLeagueId = null;
        this.selectedUserLeagueText = 'Use My League';
    },

    /**
     * Positions the sliding thumb inside a server-rendered `.btn-group__control`.
     * Call via x-effect so Alpine re-runs it whenever the relevant reactive var changes.
     * @param {HTMLElement} groupEl
     */
    syncThumb(groupEl) {
        const active = groupEl.querySelector('.btn-group__button.active');
        const thumb = groupEl.querySelector('.btn-group__thumb');
        if (!thumb) {
            return;
        }
        if (active) {
            thumb.style.transform = `translateX(${active.offsetLeft}px)`;
            thumb.style.width = `${active.offsetWidth}px`;
            thumb.style.opacity = '1';
        } else {
            thumb.style.opacity = '0';
        }
    },
    handleWeekChange(value) {
        this.selectedWeek = value;
        window.location = this.buildUrl();
    },
});
